import React from 'react';
import { useTranslation } from 'react-i18next';
import { HomeTaskAttachments } from '@/components/HomeTaskModal/HomeTaskAttachments';
import { HomeTaskCard } from '@/components/HomeTaskCard';
import type { HomeworkItem } from '@/components/HomeworksModal/buildHomeworkGroups';
import { BookFilled } from '@/components/Icons/BookFilled';
import { Paperclip } from '@/components/Icons/Paperclip';
import { useOpenThirdPartyEvent } from '@/hooks/useOpenThirdPartyEvent';
import type { Attachment } from '@/types/attachment';
import { HomeTaskType } from '@/types/components';
import { sanitizeRichTextHtml } from '@/utils/html';
import { thirdPartyTaskTypes } from '@/utils/homeTask';

interface Props {
  item: HomeworkItem;
  onOpenHomeTask: (homeTaskId: number) => void;
  onPreview: (attachment: Attachment) => void;
}

export const HomeworkCard: React.FC<Props> = ({
  item,
  onOpenHomeTask,
  onPreview,
}) => {
  const { t } = useTranslation();
  const openThirdPartyEvent = useOpenThirdPartyEvent();
  const plainTasks = item.homeTasks.filter((task) => !task.areAnswersEnabled);
  const answerTasks = item.homeTasks.filter((task) => task.areAnswersEnabled);
  const attachments = item.homeTasks.flatMap((task) => task.task.attachments);
  const hasAttachments = attachments.length > 0;

  return (
    <article className="flex flex-col gap-3 rounded-xl bg-card p-4 shadow-card contrast:border contrast:border-border">
      <div className="flex items-center gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-info-surface">
          <BookFilled className="size-4 text-primary" />
        </span>

        <h3 className="text-subhead-14 min-w-0 flex-1 truncate text-foreground">
          {item.journalName}
        </h3>

        {hasAttachments && (
          <span className="text-body-13 flex h-[22px] shrink-0 items-center gap-1 rounded-[40px] border border-transparent bg-info-surface px-2 text-primary contrast:border-border">
            <Paperclip className="size-4" />
            {attachments.length}
          </span>
        )}
      </div>

      <div className="border-t border-divider" />

      {plainTasks.map((task) => (
        <React.Fragment key={task.id}>
          {!!task.task.text && (
            <div
              className="rich-text text-body-14 text-muted-foreground"
              dangerouslySetInnerHTML={{
                __html: sanitizeRichTextHtml(task.task.text),
              }}
            />
          )}
          {!task.task.text && (
            <p className="text-body-14 text-muted-foreground">
              {t('diary.emptyDescription')}
            </p>
          )}
        </React.Fragment>
      ))}

      {answerTasks.map((task) => (
        <HomeTaskCard
          key={task.id}
          type={HomeTaskType.EKlase}
          text={task.task.text}
          hasStudentAnswer={task.hasStudentAnswer}
          onClick={() => onOpenHomeTask(task.id)}
        />
      ))}

      {item.events.map((event) => (
        <HomeTaskCard
          key={event.id}
          type={thirdPartyTaskTypes[event.applicationId] ?? HomeTaskType.EKlase}
          text={event.name}
          isDeleted={event.isDeleted}
          onClick={() => openThirdPartyEvent(event)}
        />
      ))}

      {hasAttachments && (
        <HomeTaskAttachments attachments={attachments} onPreview={onPreview} />
      )}
    </article>
  );
};
